import React, { forwardRef, useState } from 'react';
import { TagColored, TooltipTrigger, CardSurface } from '@spark';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import '@/features/datasets/components/DataSelection/NerTaggerSurface.css';

/**
 * Builds the hover-tooltip copy for an entity (Spark TooltipTrigger takes a
 * plain string, so the two-line AntD tooltip is flattened to one line).
 */
function getEntityTooltip(ent, relationshipMode, sourceEntity, targetEntity) {
    if (relationshipMode && ent.is_validated) {
        const isSource = sourceEntity?.start_index === ent.start_index;
        if (isSource) return `'${ent.label}' — selected as source`;
        if (!sourceEntity || (!targetEntity && sourceEntity.start_index !== ent.start_index)) {
            return `'${ent.label}' — click to select as target entity`;
        }
        return `'${ent.label}'`;
    }
    return ent.is_validated
        ? `'${ent.label}' — Click to unvalidate.`
        : `'${ent.label}' — Click to validate.`;
}

/**
 * Inline entity = Spark <TagColored noPadding> so a multi-line span wraps with
 * the sentence instead of becoming a nowrap chip. The tag's delete button has
 * its own "Remove …" tooltip; since the cursor is over both the entity and the
 * delete button at once, we disable the outer entity tooltip while the delete
 * button is hovered so only one tooltip shows at a time.
 */
function EntityTag({
    ent,
    tooltipText,
    variant,
    isValidated,
    showRoleTag,
    roleLabel,
    isFullScreen,
    relationshipMode,
    onClick,
    onDelete,
}) {
    const [deleteHovered, setDeleteHovered] = useState(false);

    return (
        <TooltipTrigger
            text={tooltipText}
            disabled={deleteHovered}
            className="ner-entity-tooltip"
        >
            <span
                data-index={ent.start_index}
                className={['ner-entity-wrapper', showRoleTag ? 'ner-entity-wrapper--role' : '']
                    .filter(Boolean)
                    .join(' ')}
                onClick={onClick}
                onMouseOver={(e) => setDeleteHovered(!!e.target.closest?.('.spark-tag-colored-delete'))}
                onMouseLeave={() => setDeleteHovered(false)}
            >
                <TagColored
                    label={ent.content}
                    variant={variant}
                    noPadding
                    className={['ner-entity', !isValidated ? 'ner-entity-unvalidated' : '']
                        .filter(Boolean)
                        .join(' ')}
                    showSuffixIcon={isFullScreen && !relationshipMode}
                    onDelete={onDelete}
                />
                {showRoleTag && <span className="ner-entity-role-tag">{roleLabel}</span>}
            </span>
        </TooltipTrigger>
    );
}

/**
 * NerTaggerSurface — Spark DS annotation surface.
 *
 * Presentational replacement for the legacy `.ner-tagger-text` div + EntityText.
 * It renders the sentence with inline entity highlights and handles text
 * selection (via the forwarded ref); tag selection happens in the "Available
 * tags" legend. All business logic (add/delete/validate + auto-save) stays with
 * the parent and is invoked through the passed callbacks.
 */
const NerTaggerSurface = forwardRef(function NerTaggerSurface(
    {
        text,
        entities = [],
        allCategories = [],
        isFullScreen = false,
        editingEntity = null,
        onMouseUp,
        refreshKey,
        relationshipMode = false,
        sourceEntity,
        targetEntity,
        handleToggleValidation,
        handleDeleteEntity,
        setSourceEntity,
        setTargetEntity,
        setShowRelationshipModal,
        getTextDirectionStyle,
        markedSpan = null,
    },
    ref,
) {
    const parts = [];
    let lastIdx = 0;
    const sortedEntities = [...entities].sort((a, b) => a.start_index - b.start_index);

    const markStart = markedSpan?.start_index ?? null;
    const markEnd = markStart != null ? markStart + markedSpan.text.length : null;

    const pushPlainText = (from, to, key) => {
        if (to <= from) return;
        const slice = text.slice(from, to);
        if (markStart == null || markEnd <= from || markStart >= to) {
            parts.push(
                <span key={key} data-index={from} style={getTextDirectionStyle(slice)}>
                    {slice}
                </span>,
            );
            return;
        }
        const os = Math.max(from, markStart);
        const oe = Math.min(to, markEnd);
        if (os > from) {
            parts.push(<span key={`${key}-b`} data-index={from}>{text.slice(from, os)}</span>);
        }
        parts.push(
            <span key={`${key}-m`} data-index={os} className="ner-marked-highlight">
                {text.slice(os, oe)}
            </span>,
        );
        if (to > oe) {
            parts.push(<span key={`${key}-a`} data-index={oe}>{text.slice(oe, to)}</span>);
        }
    };

    sortedEntities.forEach((ent, idx) => {
        if (ent.start_index > lastIdx) {
            pushPlainText(lastIdx, ent.start_index, `t-${idx}`);
        }

        const isValidated = ent.is_validated;
        const variant = getSparkTagVariant(ent.label, allCategories);
        const showRoleTag =
            relationshipMode &&
            (sourceEntity?.start_index === ent.start_index || targetEntity?.start_index === ent.start_index);

        const handleEntityClick = (e) => {
            // The tag's built-in delete button handles its own action.
            if (e.target.closest('.spark-tag-colored-delete')) return;
            if (relationshipMode && isValidated) {
                if (!sourceEntity) {
                    setSourceEntity(ent);
                } else if (!targetEntity && ent.start_index !== sourceEntity.start_index) {
                    setTargetEntity(ent);
                    setShowRelationshipModal(true);
                }
            } else {
                handleToggleValidation(ent);
            }
        };

        parts.push(
            <EntityTag
                key={`e-${idx}`}
                ent={ent}
                tooltipText={getEntityTooltip(ent, relationshipMode, sourceEntity, targetEntity)}
                variant={variant}
                isValidated={isValidated}
                showRoleTag={showRoleTag}
                roleLabel={sourceEntity?.start_index === ent.start_index ? 'SOURCE' : 'TARGET'}
                isFullScreen={isFullScreen}
                relationshipMode={relationshipMode}
                onClick={handleEntityClick}
                onDelete={() => handleDeleteEntity(ent)}
            />,
        );

        lastIdx = ent.start_index + ent.content.length;
    });

    if (lastIdx < text.length) {
        pushPlainText(lastIdx, text.length, 't-end');
    }

    return (
        <CardSurface
            ref={ref}
            key={refreshKey}
            background="muted"
            bordered
            padding="medium"
            className={[
                'ner-tagger-surface',
                isFullScreen ? 'ner-tagger-surface--fullscreen' : '',
                editingEntity ? 'ner-tagger-surface--editing' : '',
            ]
                .filter(Boolean)
                .join(' ')}
            onMouseUp={onMouseUp}
        >
            <span
                className="ner-tagger-surface-text"
                style={{
                    fontSize: isFullScreen ? 16 : 14,
                    ...getTextDirectionStyle(text),
                }}
            >
                {parts}
            </span>
        </CardSurface>
    );
});

export default NerTaggerSurface;
